import {
  Button,
  Flex,
  message,
  Modal,
  Space,
  Table,
  Tooltip,
  Form,
  Input,
  Select,
} from "antd";
import React, { useEffect, useState } from "react";
import { AddUserForm } from "../../../components";
import {
  deleteUser,
  getAllUsers,
  resetPasswordAdmin,
  updateUserInfo,
  updateUserSalesManager,
  updateUserSalesPerson,
  // updateUserInfo, // New service function to update user details
} from "../../../services/userDataService";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  IconCheck,
  IconEdit,
  IconKey,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { EditableCell } from "./EditableCell";

const AdminUsers = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const token = useSelector((state) => state.user.token);
  const user = useSelector((state) => state.user.user);
  const role = useSelector((state) => state.user.role);
  const [data, setData] = useState([]);
  const [addUserForm, setAddUserForm] = useState(false);
  const [buttonSize, setButtonSize] = useState("middle");
  const [filteredData, setFilteredData] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingRow, setEditingRow] = useState(null); // Track which row is being edited for user details
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedSalesManager, setSelectedSalesManager] = useState(null);
  const [selectedSalesPerson, setSelectedSalesPerson] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const handleResize = () => {
    const newSize = window.innerWidth < 900 ? "small" : "middle";
    setButtonSize(newSize);
  };

  const handleForm = () => {
    setAddUserForm(!addUserForm);
    getUsers();
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    const filtered = data.filter((item) => {
      const fullName = `${item.firstName || ""} ${
        item.lastName || ""
      }`.toLowerCase();
      const companyName = item.companyName
        ? item.companyName.toLowerCase()
        : "";
      return fullName.includes(value) || companyName.includes(value);
    });
    setFilteredData(filtered);
  };

  const getUsers = async () => {
    try {
      const res = await getAllUsers(
        role === "admin"
          ? "all"
          : role === "sales-manager"
          ? "sales-manager"
          : "sales",
        token
      );
      if (res && res.data && res.data.data) {
        setData(res.data.data);
        setFilteredData(res.data.data);
      } else {
        message.error("Error fetching users", res);
      }
    } catch (error) {
      message.error("Error occurred while fetching users");
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  const getUniqueValues = (data, key) => {
    return [...new Set(data.map((item) => item[key]))].map((value) => ({
      text: value,
      value,
    }));
  };

  const handleEditClick = (user) => {
    setEditingUserId(user.id);
    setSelectedSalesManager(user.salesManager?.id || null);
    setSelectedSalesPerson(user.salesPerson?.id || null);
  };

  // Start editing a row for user details
  const startEditUserDetails = (record) => {
    // Set form values with current data
    editForm.setFieldsValue({
      name: `${record.firstName || ""} ${record.lastName || ""}`.trim(),
      email: record.email || "",
      phone: record.phone || "",
      companyName: record.companyName || "",
    });

    setEditingRow(record.id);
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingRow(null);
    editForm.resetFields();
  };

  // Save edited user details
  // const saveUserDetails = async (record) => {
  //   try {
  //     const values = await editForm.validateFields();

  //     // Create update object
  //     let updateData = { ...values };

  //     // Split name into firstName and lastName
  //     if (values.name) {
  //       const nameParts = values.name.trim().split(/\s+/);
  //       if (nameParts.length >= 2) {
  //         updateData.firstName = nameParts[0];
  //         updateData.lastName = nameParts.slice(1).join(" ");
  //       } else if (nameParts.length === 1) {
  //         updateData.firstName = nameParts[0];
  //         updateData.lastName = "";
  //       }
  //       delete updateData.name;
  //     }

  //     await updateUserInfo(token, record.id, updateData);

  //     message.success(`User details updated successfully`);

  //     setEditingRow(null); // ✅ only reset if successful
  //     setEditingUserId(null);
  //     getUsers(); // Refresh user list
  //   } catch (error) {
  //     message.error(
  //       error.response?.data?.message ||
  //         `Failed to update user details: ${error.message || "Unknown error"}`
  //     );
  //     // ❌ Don't reset `editingRow` on failure
  //   }
  // };

  const handleCancel = () => {
    setEditingUserId(null);
  };

  const handleFilter = (pagination, filters, sorter) => {
    let filtered = data;

    if (filters.role) {
      filtered = filtered.filter((item) => filters.role.includes(item.role));
    }
    if (filters.name) {
      filtered = filtered.filter((item) => {
        const name = `${item.firstName || ""} ${item.lastName || ""}`;
        return filters.name.some((filterName) => name.includes(filterName));
      });
    }
    if (filters.salesPerson) {
      filtered = filtered.filter((item) => {
        if (!item.salesPerson) return false;
        const salesPersonName = `${item.salesPerson.firstName || ""} ${
          item.salesPerson.lastName || ""
        }`;
        return filters.salesPerson.some((filterName) =>
          salesPersonName.includes(filterName)
        );
      });
    }

    setFilteredData(filtered);
  };

  const handleResetPassword = (user) => {
    Modal.confirm({
      title: `Reset Password for ${user.firstName || ""} ${
        user.lastName || ""
      }?`,
      content: "Are you sure you want to reset the password for this user?",
      onOk: () => {
        setSelectedUser(user);
        setIsPasswordModalVisible(true);
      },
    });
  };

  const handleDeleteUser = (user) => {
    Modal.confirm({
      title: `Delete User ${user.firstName || ""} ${user.lastName || ""}?`,
      content: "Are you sure you want to delete this user?",
      onOk: async () => {
        try {
          await deleteUser(token, user.id);
          message.success(
            `User ${user.firstName || user.companyName} deleted successfully`
          );

          // Filter out the deleted user from state
          setData((prev) => prev.filter((u) => u.id !== user.id));
          setFilteredData((prev) => prev.filter((u) => u.id !== user.id));
        } catch (err) {
          message.error("Error deleting user");
        }
      },
    });
  };

  const handlePasswordSubmit = async (values) => {
    try {
      const response = await resetPasswordAdmin(
        token,
        selectedUser.id,
        values.password
      );
      message.success(
        `Password reset successfully for ${
          selectedUser.firstName || selectedUser.companyName
        }`
      );
      setIsPasswordModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error("Error resetting password");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      align: "center",
      render: (text, record) => {
        const isEditing = record.id === editingRow;
        return isEditing ? (
          <Form.Item
            name="name"
            style={{ margin: 0 }}
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input />
          </Form.Item>
        ) : (
          `${record.firstName || ""} ${record.lastName || ""}`
        );
      },
      filters: getUniqueValues(filteredData, "firstName").map((item) => ({
        text: `${item.value} ${
          filteredData.find((d) => d.firstName === item.value).lastName
        }`,
        value: `${item.value} ${
          filteredData.find((d) => d.firstName === item.value).lastName
        }`,
      })),
      onFilter: (value, record) => {
        const name = `${record.firstName} ${record.lastName}`;
        return name.includes(value);
      },
    },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
      align: "center",
      filters: getUniqueValues(filteredData, "companyName"),
      onFilter: (value, record) => record.companyName === value,
      render: (text, record) => {
        // const isEditing = record.id === editingRow;
        // return isEditing ? (
        //   <Form.Item name="companyName" style={{ margin: 0 }}>
        //     <Input />
        //   </Form.Item>
        // ) : (
        //   text || "-"
        // );
        return text || "-";
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      align: "center",
      render: (text, record) => {
        const isEditing = record.id === editingRow;
        return isEditing ? (
          <Form.Item
            name="email"
            style={{ margin: 0 }}
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input />
          </Form.Item>
        ) : (
          text || "-"
        );
      },
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      align: "center",
      render: (text, record) => {
        const isEditing = record.id === editingRow;
        return isEditing ? (
          <Form.Item name="phone" style={{ margin: 0 }}>
            <Input />
          </Form.Item>
        ) : (
          text || "-"
        );
      },
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      align: "center",
      filters: getUniqueValues(filteredData, "role"),
      onFilter: (value, record) => record.role === value,
    },
  ];

  if (role === "admin" || role === "sales-manager") {
    if (role === "admin") {
      // Only Admin can see & edit Sales Manager
      columns.push({
        title: "Sales Manager",
        dataIndex: "salesManager",
        key: "salesManager",
        align: "center",
        filters: Array.from(
          new Set(
            data
              .filter((item) => item.salesManager)
              .map((item) => {
                const fullName = `${item.salesManager.firstName} ${item.salesManager.lastName}`;
                return fullName;
              })
          )
        ).map((name) => ({
          text: name,
          value: name,
        })),

        onFilter: (value, record) => {
          if (!record.salesManager) return false;
          const name = `${record.salesManager.firstName} ${record.salesManager.lastName}`;
          return name.includes(value);
        },
        render: (text, record) => {
          // We only show the Select if the row is being edited:
          if (editingUserId === record.id) {
            // 1) If the row user is "sales", populate with "sales-manager" users
            if (record.role === "sales") {
              return (
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select Sales Manager"
                  value={selectedSalesManager}
                  onChange={(value) => setSelectedSalesManager(value)}
                >
                  {data
                    .filter(
                      (u) => u.role === "sales-manager" || u.role === "admin"
                    )
                    .map((manager) => (
                      <Select.Option key={manager.id} value={manager.id}>
                        {manager.firstName} {manager.lastName}
                      </Select.Option>
                    ))}
                </Select>
              );
            }

            // 2) If the row user is "sales-manager", populate with "admin" users
            if (record.role === "sales-manager") {
              return (
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select Admin"
                  value={selectedSalesManager}
                  onChange={(value) => setSelectedSalesManager(value)}
                >
                  {data
                    .filter((u) => u.role === "admin")
                    .map((adminUser) => (
                      <Select.Option key={adminUser.id} value={adminUser.id}>
                        {adminUser.firstName} {adminUser.lastName}
                      </Select.Option>
                    ))}
                </Select>
              );
            }
          }

          // If not in edit mode or none of the above roles:
          if (record.salesManager) {
            return `${record.salesManager.firstName} ${record.salesManager.lastName}`;
          }

          // 2) Otherwise, if there's a nested manager under `record.salesPerson.salesManager`, show that name
          if (record.salesPerson?.salesManager) {
            return `${record.salesPerson.salesManager.firstName} ${record.salesPerson.salesManager.lastName}`;
          }

          // 3) Fallback if neither exists
          return "-";
        },
      });
    }

    // Sales Person column remains visible for both Admin & Sales Manager
    columns.push({
      title: "Sales Person",
      dataIndex: "salesPerson",
      key: "salesPerson",
      align: "center",
      filters: Array.from(
        new Set(
          data
            .filter((item) => item.salesPerson)
            .map((item) => {
              const spName = `${item.salesPerson.firstName} ${item.salesPerson.lastName}`;
              return spName;
            })
        )
      ).map((name) => ({
        text: name,
        value: name,
      })),

      onFilter: (value, record) => {
        if (!record.salesPerson) return false;
        const spName = `${record.salesPerson.firstName} ${record.salesPerson.lastName}`;
        return spName.includes(value);
      },
      render: (text, record) => {
        // Only allow editing if:
        // 1) The row user's role is 'customer'
        // 2) The row is currently in edit mode
        if (record.role === "customer" && editingUserId === record.id) {
          return (
            <Select
              style={{ width: "100%" }}
              placeholder="Select Sales Person"
              value={selectedSalesPerson}
              onChange={(value) => setSelectedSalesPerson(value)}
            >
              <Select.Option value={user.id}>
                {user.firstName} {user.lastName}
              </Select.Option>
              {data
                .filter((u) => u.role === "sales" || u.role === "sales-manager")
                .map((person) => (
                  <Select.Option key={person.id} value={person.id}>
                    {person.firstName} {person.lastName}
                  </Select.Option>
                ))}
            </Select>
          );
        }

        // Otherwise, show read-only text
        return record.salesPerson
          ? `${record.salesPerson.firstName} ${record.salesPerson.lastName}`
          : "-";
      },
    });
  }

  if (role === "admin" || role === "sales-manager") {
    columns.push({
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => {
        // If row user is admin => no edit
        if (
          record.email === "sachinkaythamwar@gmail.com" ||
          (record.email === "ramesh.iyer@richlinegroup.com" &&
            user.email !== "ramesh.iyer@richlinegroup.com")
        ) {
          return <p>-</p>;
        }

        const isEditing = record.id === editingRow;

        // If row user is 'sales', 'customer', or 'sales-manager', show edit button
        return (
          (role === "admin" || role === "sales-manager") && (
            <Flex justify="center" gap={5}>
              {/* Edit user details (name, email, phone) */}

              {/* Edit assignment (Sales Manager/Person relationship) */}
              {editingUserId === record.id ? (
                <>
                  <Tooltip title="Save Assignment" placement="bottom">
                    <Button
                      size={buttonSize}
                      type="primary"
                      onClick={async () => {
                        try {
                          // First, run the appropriate assignment update
                          if (
                            record.role === "sales" ||
                            record.role === "sales-manager"
                          ) {
                            await handleSaveSalesManager(
                              record,
                              selectedSalesManager,
                              false
                            ); // 👈 no message inside
                          } else if (record.role === "customer") {
                            await handleSaveSalesPerson(
                              record,
                              selectedSalesPerson,
                              false
                            ); // 👈 no message inside
                          }

                          await saveUserDetails(record, false); // 👈 no message inside

                          message.success("User updated successfully"); // ✅ One clean toast
                          setEditingUserId(null);
                          setEditingRow(null);
                          getUsers();
                        } catch (error) {
                          message.error(
                            error?.response?.data?.message ||
                              "Update failed. Please try again."
                          );
                        }
                      }}
                    >
                      <IconCheck stroke={2} />
                    </Button>
                  </Tooltip>
                  <Tooltip title="Cancel Assignment" placement="bottom">
                    <Button
                      size={buttonSize}
                      danger
                      onClick={() => {
                        handleCancel();
                        cancelEdit();
                      }}
                    >
                      <IconX stroke={2} />
                    </Button>
                  </Tooltip>
                </>
              ) : (
                <Tooltip title="Edit Assignment" placement="bottom">
                  <Button
                    size={buttonSize}
                    onClick={() => {
                      handleEditClick(record);
                      if (role === "admin") {
                        startEditUserDetails(record);
                      }
                    }}
                    disabled={editingRow !== null || editingUserId !== null} // Disable if another row is being edited
                  >
                    <IconEdit stroke={2} />
                  </Button>
                </Tooltip>
              )}

              {role === "admin" && (
                <>
                  <Tooltip title="Reset Password" placement="bottom">
                    <Button
                      size={buttonSize}
                      onClick={() => handleResetPassword(record)}
                    >
                      <IconKey stroke={2} />
                    </Button>
                  </Tooltip>
                  <Tooltip title="Delete User" placement="bottom">
                    <Button
                      size={buttonSize}
                      onClick={() => handleDeleteUser(record)}
                    >
                      <IconTrash stroke={2} />
                    </Button>
                  </Tooltip>
                </>
              )}
            </Flex>
          )
        );
      },
    });
  }

  // const handleSaveSalesManager = async (record, newSalesManagerId) => {
  //   try {
  //     const response = await updateUserSalesManager(
  //       token,
  //       record.id,
  //       newSalesManagerId
  //     );
  //     message.success("Sales Manager updated successfully");
  //     getUsers();
  //   } catch (error) {
  //     message.error("Error updating Sales Manager");
  //   }
  // };

  // const handleSaveSalesPerson = async (record, newSalesPersonId) => {
  //   try {
  //     const response = await updateUserSalesPerson(
  //       token,
  //       record.id,
  //       newSalesPersonId
  //     );

  //     message.success("Updated sales person successfully");
  //     getUsers();
  //   } catch (error) {
  //     message.error("Error updating Sales Person");
  //   }
  // };

  const handleSaveSalesManager = async (
    record,
    newSalesManagerId,
    showMessage = true
  ) => {
    try {
      await updateUserSalesManager(token, record.id, newSalesManagerId);
      if (showMessage) message.success("Sales Manager updated successfully");
    } catch (error) {
      message.error("Error updating Sales Manager");
      throw error;
    }
  };

  const handleSaveSalesPerson = async (
    record,
    newSalesPersonId,
    showMessage = true
  ) => {
    try {
      await updateUserSalesPerson(token, record.id, newSalesPersonId);
      if (showMessage) message.success("Sales Person updated successfully");
    } catch (error) {
      message.error("Error updating Sales Person");
      throw error;
    }
  };

  const saveUserDetails = async (record, showMessage = true) => {
    try {
      const values = await editForm.validateFields();
      let updateData = { ...values };

      if (values.name) {
        const trimmedName = values.name.trim().replace(/\s+/g, " ");
        const spaceIndex = trimmedName.indexOf(" ");

        if (spaceIndex !== -1) {
          updateData.firstName = trimmedName.slice(0, spaceIndex);
          updateData.lastName = trimmedName.slice(spaceIndex + 1);
        } else {
          updateData.firstName = trimmedName;
          updateData.lastName = "";
        }

        delete updateData.name;
      }

      await updateUserInfo(token, record.id, updateData);

      if (showMessage) message.success("User details updated successfully");
    } catch (error) {
      message.error(
        error.response?.data?.message ||
          `Failed to update user details: ${error.message || "Unknown error"}`
      );
      throw error;
    }
  };

  return (
    <div>
      {!addUserForm && (
        <>
          <Space size="middle" style={{ marginBottom: 16 }}>
            <Flex wrap gap={10}>
              <Input
                placeholder="Search by Name or Company"
                value={searchText}
                onChange={handleSearch}
                style={{ width: "300px" }}
              />
              <Button type="primary" onClick={handleForm}>
                + Add User
              </Button>
              {(role === "admin" || role === "sales-manager") && (
                <>
                  <Button
                    onClick={() =>
                      setFilteredData(
                        data.filter((item) => item.role === "sales")
                      )
                    }
                  >
                    Show All Sales
                  </Button>
                  <Button
                    onClick={() =>
                      setFilteredData(
                        data.filter((item) => item.role === "customer")
                      )
                    }
                  >
                    Show All Customers
                  </Button>
                  <Button onClick={() => setFilteredData(data)}>
                    Show All Users
                  </Button>
                </>
              )}
            </Flex>
          </Space>
          <Form form={editForm} component={false}>
            <Table
              columns={columns}
              dataSource={filteredData}
              onChange={handleFilter}
              rowKey="id"
              rowClassName={(record) =>
                record.id === editingRow || record.id === editingUserId
                  ? "editable-row"
                  : ""
              }
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
              }}
            />
          </Form>
        </>
      )}
      {addUserForm && (
        <>
          <AddUserForm handleClick={handleForm} />
        </>
      )}

      {/* Password Modal */}
      <Modal
        title={`Reset Password for ${
          selectedUser ? selectedUser.firstName : ""
        }`}
        visible={isPasswordModalVisible}
        onCancel={() => setIsPasswordModalVisible(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} onFinish={handlePasswordSubmit}>
          <Form.Item
            name="password"
            label="New Password"
            rules={[
              { required: true, message: "Please enter the new password" },
              {
                pattern:
                  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                message:
                  "Password must be at least 8 characters long, include an uppercase letter, a lowercase letter, a number, and a special character.",
              },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminUsers;
